import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { REPORTS_LIST_QUERY_KEY } from '@/modules/common/queries/reports.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  CreateBatchReportsRequest,
  DownloadMergedBatchReportsRequest,
  DownloadMergedBatchReportsResponse,
  GetBatchDetailsResponse,
  GetBatchDetailsV1ReportsBatchIdGetParams,
  GetBatchReportsListResponse,
  GetBatchReportsListV1ReportsBatchGetParams,
  ReportStatus,
  UpdateBatchReportRequest,
} from 'compliance-api';

export const REPORTS_BATCH_LIST_QUERY_KEY = 'compliance/reports-batch-list';
export const REPORT_BATCH_ZIP_ARCHIVE_QUERY_KEY = 'compliance/reports-batch-zip-archive';
export const CURRENT_REPORTS_BATCH_QUERY_KEY = 'compliance/reports-batch';

export const useGetReportsBatchListQuery = (
  payload: GetBatchReportsListV1ReportsBatchGetParams,
  disabled = false,
) => {
  return useInfiniteQuery<GetBatchReportsListResponse>({
    queryKey: [REPORTS_BATCH_LIST_QUERY_KEY, JSON.stringify(payload), useGetCurrentBusinessId()],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getBatchReportsListV1ReportsBatchGet({
        ...payload,
        page: pageParam || payload.page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useGetBatchReportInfiniteQuery = (
  payload: GetBatchDetailsV1ReportsBatchIdGetParams,
  disabled = false,
) => {
  const { page, ...data } = payload;
  return useInfiniteQuery<GetBatchDetailsResponse>({
    queryKey: [CURRENT_REPORTS_BATCH_QUERY_KEY, JSON.stringify(data), useGetCurrentBusinessId()],
    staleTime: 999 * 60 * 60, // 1 hour
    keepPreviousData: true,
    retry: 2,
    refetchInterval: data => {
      const reports = (data?.pages || []).flatMap(({ reports }) => reports);
      const haveGenerating = reports?.some(item => item.status === ReportStatus.Generating);
      if (haveGenerating) return 6000;
      return false;
    },
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getBatchDetailsV1ReportsBatchIdGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useGetBatchReportQuery = ({
  payload,
  disabled = false,
  onCompleted,
}: {
  payload: GetBatchDetailsV1ReportsBatchIdGetParams;
  disabled?: boolean;
  onCompleted?: (data?: GetBatchDetailsResponse) => number | false;
}) => {
  return useQuery<GetBatchDetailsResponse>({
    queryKey: [CURRENT_REPORTS_BATCH_QUERY_KEY, JSON.stringify(payload), useGetCurrentBusinessId()],
    staleTime: 999 * 60 * 60, // 1 hour
    keepPreviousData: true,
    retry: 2,
    refetchInterval: onCompleted
      ? data => {
          return onCompleted(data);
        }
      : 999 * 60 * 60,
    queryFn: async () => {
      const response = await api.v1.getBatchDetailsV1ReportsBatchIdGet(payload);
      return response.data;
    },

    enabled: !disabled,
  });
};

export const useGenerateBatchReportsMutation = ({
  onSuccess,
}: {
  onSuccess?: (data: DownloadMergedBatchReportsResponse) => Promise<void> | void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: DownloadMergedBatchReportsRequest) => {
      const response = await withErrorHandling(
        api.v1.downloadCombinedBatchReportsV1ReportsBatchCombinedPost(payload),
        { loading: 'Generation batch...', success: 'Batch generated successfully.' },
      );
      return response.data;
    },
    onSuccess: async data => {
      queryClient.invalidateQueries([REPORTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([REPORTS_BATCH_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([REPORT_BATCH_ZIP_ARCHIVE_QUERY_KEY]);
      onSuccess && (await onSuccess(data));
      await queryClient.resetQueries({ queryKey: [CURRENT_REPORTS_BATCH_QUERY_KEY] });
    },
  });
};

export const useDownloadZipArchiveBatchReportsQuery = (
  id: number,
  { disabled = false, onSuccess }: { disabled?: boolean; onSuccess?: (data: any) => void },
) => {
  return useQuery({
    queryKey: [REPORT_BATCH_ZIP_ARCHIVE_QUERY_KEY, id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.downloadBatchReportsV1ReportsBatchIdDownloadGet(
        { id },
        {
          format: 'blob',
        },
      );
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
    onSuccess,
  });
};

export const useCreateReportsBatchMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateBatchReportsRequest) => {
      const response = await withErrorHandling(
        api.v1.createBatchReportV1ReportsBatchPost(payload),
        { loading: 'Creating batch...', success: 'Batch created' },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([REPORTS_BATCH_LIST_QUERY_KEY]);
    },
  });
};

export const useUpdateReportsBatchMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateBatchReportRequest) => {
      const response = await withErrorHandling(
        api.v1.updateBatchReportV1ReportsBatchPatch(payload),
        { loading: 'Updating batch...', success: 'Batch updated' },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([REPORTS_BATCH_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_REPORTS_BATCH_QUERY_KEY]);
    },
  });
};

export const useDeleteReportsBatchMutation = () => {
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const response = await withErrorHandling(
        api.v1.deleteBatchReportV1ReportsBatchIdDelete({ id }),
        {
          loading: 'Deleting batch...',
          success: 'Batch deleted',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([REPORTS_BATCH_LIST_QUERY_KEY]);
    },
  });
};
